import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLines, Eyebrow } from "./motion";
import { HOUSES } from "./Houses";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-2">
    <span className="eyebrow text-[10px] text-[#8C6A55]">{label}</span>
    {children}
  </label>
);

const inputCls = "bg-transparent border-b border-[#CFC6B8] focus:border-[#B85D3B] outline-none py-3 text-[#1C1D1A] transition-colors placeholder:text-[#9A9C94]";

export const Booking = ({ form, setForm }) => {
  const [loading, setLoading] = useState(false);
  const b = useLang().t.booking;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.checkin && form.checkout && form.checkout <= form.checkin) {
      toast.error(b.errDates);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, guests: Number(form.guests) });
      toast.success(b.ok);
      setForm({ name: "", email: "", phone: "", house: "marias", checkin: "", checkout: "", guests: "2", notes: "" });
    } catch (err) {
      toast.error(b.err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking" data-testid="booking-section" className="bg-[#F7F5F0] py-24 md:py-40">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Eyebrow>{b.eyebrow}</Eyebrow>
          <MaskedLines lines={[b.l1, <em key="e" className="italic text-[#B85D3B]">{b.l2}</em>]} className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight mt-6 text-[#1C1D1A]" />
          <Reveal delay={0.2}>
            <p className="mt-8 text-[#5C5E56] leading-relaxed max-w-md">{b.intro}</p>
            <div className="mt-10 arch overflow-hidden aspect-[4/3] max-w-md hidden sm:block">
              <img src="/img/casas/coracao2/20.jpg" alt="Terraço da Casa Coração Alentejano com vista para o campo" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
        <Reveal className="lg:col-span-6 lg:col-start-7">
          <form onSubmit={submit} data-testid="booking-form" className="bg-[#FFFFFF] rounded-[28px] p-7 md:p-12 border border-[#E2DDD5] grid sm:grid-cols-2 gap-x-8 gap-y-7">
            <Field label={b.name}><input required minLength={2} data-testid="booking-form-name" value={form.name} onChange={set("name")} className={inputCls} placeholder={b.namePh} /></Field>
            <Field label={b.email}><input required type="email" data-testid="booking-form-email" value={form.email} onChange={set("email")} className={inputCls} placeholder="email@exemplo.pt" /></Field>
            <Field label={b.phone}><input data-testid="booking-form-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+351 ..." /></Field>
            <Field label={b.house}>
              <select data-testid="booking-form-house" value={form.house} onChange={set("house")} className={inputCls}>
                {HOUSES.map((h) => <option key={h.id} value={h.id}>{h.name} ({h.type})</option>)}
              </select>
            </Field>
            <Field label="Check-in"><input required type="date" data-testid="booking-form-checkin" value={form.checkin} onChange={set("checkin")} className={inputCls} /></Field>
            <Field label="Check-out"><input required type="date" data-testid="booking-form-checkout" value={form.checkout} onChange={set("checkout")} className={inputCls} /></Field>
            <Field label={b.guests}>
              <select data-testid="booking-form-guests" value={form.guests} onChange={set("guests")} className={inputCls}>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </Field>
            <div className="sm:col-span-2">
              <Field label={b.notes}><textarea data-testid="booking-form-notes" rows={3} value={form.notes} onChange={set("notes")} className={`${inputCls} resize-none`} placeholder={b.notesPh} /></Field>
            </div>
            <button data-testid="booking-form-submit" disabled={loading} type="submit" className="sm:col-span-2 btn-pill justify-center bg-[#1C1D1A] text-[#F7F5F0] hover:bg-[#B85D3B] py-5 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{b.submit} <ArrowUpRight className="w-4 h-4" /></>}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
